import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import { clearTranslationDrafts } from '../../../utils/drafts';
import { findSimilarSegments } from '../../../utils/similarity';
import { languageToolLocales } from '../../languageToolLocales';
import AnnotationPanel from '../../AnnotationPanel';
import CommentsPanel from './CommentsPanel';
import ContextSyncAlert from '../../ContextSyncAlert';
import ContextSyncModal from '../../ContextSyncModal';
import ProjectMapOverlay from '../../ProjectMapOverlay';
import '../../DashboardHome/rush.css';
import '../../DashboardHome/dashboard.css';
import '../../DashboardHome/rush-fixes.css';

// ============ ICONS (preserve design) ============
const EIcn = {
    back: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M8 3L4 7l4 4M4 7h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    caret: (
        <svg className="caret" width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    check: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    edit: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M2 11l2-.5L11 4l-1.5-1.5L3 9l-1 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    reject: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    ai: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2l1.4 3.1L12.5 6 9.4 7.4 8 10.5 6.6 7.4 3.5 6l3.1-.9L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    memory: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    glossary: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M3 3h7l3 3v7H3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M5 7h6M5 9.5h6M5 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    comment: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 8a5 5 0 015-5h1a5 5 0 015 5v3a1 1 0 01-1 1H8L5 14v-2H3.5a1 1 0 01-1-1V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    alert: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.7" fill="currentColor" />
        </svg>
    ),
    okCirc: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    warn: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.7" fill="currentColor" />
        </svg>
    ),
    filter: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M2 3h10l-4 5v4l-2-1V8L2 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
};

const STATUS_LABEL = {
    approved: 'Approved',
    reviewed: 'Reviewed',
    translated: 'Translated',
    flagged: 'Flagged',
    untranslated: 'Untranslated',
};

const STATUS_FILTERS = ['All', 'Untranslated', 'Translated', 'Reviewed', 'Approved', 'Flagged'];

// ---- Language helpers (mirrors TranslateProject) ----
const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === (code || '').toLowerCase()) ||
    { name: code || '', countryCode: 'UN' };

const isLanguageRTL = (code) => getLanguageDetails(code).direction === 'rtl';

const getLanguageToolLocale = (src) => {
    if (!src) return 'en-GB';
    const norm = src.toLowerCase();
    let entry = languageToolLocales.find((l) => l.longCode.toLowerCase() === norm);
    if (entry) return entry.longCode;
    entry = languageToolLocales.find((l) => l.longCode.toLowerCase().startsWith(norm));
    if (entry) return entry.longCode;
    entry = languageToolLocales.find((l) => l.code.toLowerCase() === norm.split(/[-_]/)[0]);
    return entry ? entry.longCode : 'en-GB';
};

const tmCls = (pct) => {
    if (pct == null) return 'low';
    if (pct >= 90) return '';
    if (pct >= 75) return 'med';
    return 'low';
};

const initialsOf = (n) => (n || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

// ============ TOP BAR ============
const TopBar = ({ projectName, fileExt, approvedCount, total, current, sourceLang, targetLang, saving, onBack }) => {
    const pct = total ? Math.round((approvedCount / total) * 100) : 0;
    const e = (fileExt || 'doc').toUpperCase().slice(0, 3);
    return (
        <div className="rs-ed-bar">
            <button type="button" className="back" onClick={onBack}>
                {EIcn.back}<span>Back</span>
            </button>
            <div className="file-ic">{e}</div>
            <div className="title-block">
                <div className="pn">{projectName || 'Untitled project'}</div>
                <div className="crm">{total} segments</div>
            </div>
            <div className="rs-ed-lang-switch">
                <span className="pair">{sourceLang} <span className="arr">→</span> {targetLang}</span>
                {EIcn.caret}
            </div>
            <div className="rs-ed-progress">
                <div className="pmeta">
                    <span>Segment <b>{(current ?? 0) + 1}</b> / <b>{total}</b></span>
                    <span><b>{pct}%</b> complete</span>
                </div>
                <div className="pbar"><span style={{ width: `${pct}%` }} /></div>
            </div>
            <span className="spacer" />
            <span className="save-status">
                <span className="dt" style={{ background: saving ? 'var(--accent)' : 'var(--green)' }} />
                {saving ? 'Saving…' : 'All changes saved'}
            </span>
        </div>
    );
};

// ============ NAVIGATOR ============
const Navigator = ({ sections, deriveStatus, active, setActive, filter, setFilter, statusCounts }) => {
    const items = sections.map((src, i) => ({ src, i, status: deriveStatus(i) }));
    const filtered = filter === 'All' ? items : items.filter((it) => STATUS_LABEL[it.status] === filter);

    return (
        <aside className="rs-ed-snav">
            <div className="rs-ed-snav-h">
                <h3>Segments — {filtered.length}</h3>
                <div className="rs-ed-snav-filters">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={`rs-ed-snav-filter ${filter === f ? 'on' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f} <span style={{ opacity: 0.7 }}>{statusCounts[f] || 0}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="rs-ed-snav-list">
                {filtered.length === 0 && (
                    <div style={{ padding: 24, color: 'var(--ink-4)', fontSize: 12.5, textAlign: 'center' }}>
                        Nothing matches this filter.
                    </div>
                )}
                {filtered.map(({ src, i, status }) => (
                    <button
                        type="button"
                        key={i}
                        className={`rs-ed-snav-item ${active === i ? 'active' : ''}`}
                        onClick={() => setActive(i)}
                    >
                        <span className="sn">{i + 1}</span>
                        <span className="stxt">{(src || '').slice(0, 80)}</span>
                        <span className={`sdot ${status}`} />
                    </button>
                ))}
            </div>
        </aside>
    );
};

// ============ GLOSSARY HIGHLIGHTING ============
const renderWithGloss = (text, glossList = []) => {
    if (!glossList.length || !text) return text;
    const escaped = glossList.map((g) => g.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
    return text.split(pattern).map((p, i) => {
        const hit = glossList.some((g) => g.toLowerCase() === p.toLowerCase());
        return hit
            ? <span key={i} className="gloss" title="Glossary term">{p}</span>
            : <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

// ============ ACTIVE SEGMENT ============
const FLAG_QUICKPICKS = [
    'Ambiguous source',
    'Terminology doubt',
    'Needs context',
    'Tone uncertain',
];

const FlagIcon = (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2.5 1.5v9M2.5 2h6.2l-1.4 2 1.4 2H2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ActiveSegment = ({
    index, total, source, target, isApproved, confidence,
    sourceLang, targetLang, sourceDir, targetDir,
    glossTerms,
    aiSuggestion, aiSourceLabel, aiLoading,
    onChange, onApprove, onUnapprove, onReject, onMarkReview,
    onAcceptAI, onDismissAI, onRegenerateAI,
    flagInfo, onFlag, onResolveFlag,
}) => {
    const taRef = useRef(null);
    const reasonRef = useRef(null);
    const [showFlagForm, setShowFlagForm] = useState(false);
    const [reasonInput, setReasonInput] = useState('');

    useEffect(() => {
        if (taRef.current) taRef.current.focus();
    }, [index]);

    // Close the flag form whenever we move to a different segment.
    useEffect(() => {
        setShowFlagForm(false);
        setReasonInput('');
    }, [index]);

    useEffect(() => {
        if (showFlagForm && reasonRef.current) reasonRef.current.focus();
    }, [showFlagForm]);

    // Auto-grow the target textarea to fit content (no inner scrollbar).
    useEffect(() => {
        const el = taRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [target, index]);

    const status = flagInfo
        ? 'flagged'
        : (isApproved ? 'approved' : (target ? 'translated' : 'untranslated'));
    const tmDisplay = confidence != null ? `${Math.round(confidence)}%` : '—';

    const submitFlag = () => {
        onFlag(index, reasonInput);
        setShowFlagForm(false);
        setReasonInput('');
    };

    const cancelFlagForm = () => {
        setShowFlagForm(false);
        setReasonInput('');
    };

    const handleMarkReviewClick = () => {
        setShowFlagForm((v) => !v);
        if (onMarkReview) onMarkReview();
    };

    return (
        <div className="rs-ed-seg active">
            {flagInfo && (
                <div className="a-flagbar" role="status">
                    <span className="a-flagbar-ic">{FlagIcon}</span>
                    <div className="a-flagbar-body">
                        <div className="a-flagbar-lbl">Flagged for review</div>
                        <div className={`a-flagbar-reason ${flagInfo.reason ? '' : 'empty'}`}>
                            {flagInfo.reason || 'No reason given'}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="a-btn ghost"
                        onClick={() => onResolveFlag(index)}
                    >
                        {EIcn.check} Resolve
                    </button>
                </div>
            )}

            <div className="a-top">
                <span className="a-num">Segment <b>{index + 1}</b> / {total}</span>
                <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
                <span className="spacer" />
                <span className="a-meta">
                    <b>{tmDisplay}</b> match
                    <span style={{ width: 3, height: 3, borderRadius: 999, background: 'var(--ink-4)' }} />
                    <b>{sourceLang} → {targetLang}</b>
                </span>
            </div>

            <div className="a-panes">
                <div className="a-pane src">
                    <div className="a-lbl">Source · {sourceLang}</div>
                    <div className="a-text" dir={sourceDir}>{renderWithGloss(source, glossTerms.map((g) => g.source))}</div>
                </div>
                <div className="a-pane tgt">
                    <div className="a-lbl">Target · {targetLang}</div>
                    <textarea
                        ref={taRef}
                        className="a-input"
                        dir={targetDir}
                        value={target || ''}
                        placeholder="Translate this segment…"
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                e.preventDefault();
                                onApprove();
                            }
                        }}
                    />
                </div>
            </div>

            {(aiSuggestion || aiLoading) && (
                <div className="a-ai">
                    <div className="ai-ic">{EIcn.ai}</div>
                    <div className="ai-body">
                        <div className="ai-lbl">
                            AI suggestion · {aiSourceLabel || 'Rush AI'}
                            {aiLoading && <span style={{ marginLeft: 8, color: 'var(--ink-4)' }}>generating…</span>}
                        </div>
                        {aiSuggestion && (
                            <>
                                <div className="ai-txt">{renderWithGloss(aiSuggestion, glossTerms.map((g) => g.source))}</div>
                                <div className="ai-actions">
                                    <button type="button" className="ai-btn primary" onClick={() => onAcceptAI(aiSuggestion)}>
                                        {EIcn.check} Replace
                                    </button>
                                    <button type="button" className="ai-btn" onClick={() => onChange((target || '') + ((target && !target.endsWith(' ')) ? ' ' : '') + aiSuggestion)}>
                                        Insert at cursor
                                    </button>
                                    <button type="button" className="ai-btn" onClick={onRegenerateAI}>
                                        Regenerate
                                    </button>
                                    <button type="button" className="ai-btn" onClick={onDismissAI}>
                                        Dismiss
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showFlagForm && !flagInfo && (
                <div
                    className="a-flagform"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') { e.preventDefault(); cancelFlagForm(); }
                    }}
                >
                    <label className="a-flagform-lbl" htmlFor="rs-flag-reason">
                        Why flag this segment?
                    </label>
                    <input
                        id="rs-flag-reason"
                        ref={reasonRef}
                        type="text"
                        className="a-flagform-input"
                        placeholder="ambiguous source, terminology doubt, missing context…"
                        value={reasonInput}
                        onChange={(e) => setReasonInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); submitFlag(); }
                        }}
                    />
                    <div className="a-flagform-chips">
                        {FLAG_QUICKPICKS.map((q) => (
                            <button
                                key={q}
                                type="button"
                                className="a-flagform-chip"
                                onClick={() => {
                                    setReasonInput(q);
                                    if (reasonRef.current) reasonRef.current.focus();
                                }}
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                    <div className="a-flagform-actions">
                        <span className="spacer" />
                        <button type="button" className="a-btn ghost" onClick={cancelFlagForm}>
                            Cancel
                        </button>
                        <button type="button" className="a-btn primary" onClick={submitFlag}>
                            {FlagIcon} Flag for review
                        </button>
                    </div>
                </div>
            )}

            <div className="a-foot">
                <span className="hint"><span className="kbd">⌘</span> + <span className="kbd">⏎</span> confirm & next</span>
                <span className="hint"><span className="kbd">⌘</span> + <span className="kbd">.</span> skip</span>
                <span className="spacer" />
                <button type="button" className="a-btn warn" onClick={onReject}>
                    {EIcn.reject} Reject
                </button>
                {isApproved ? (
                    <button type="button" className="a-btn ghost" onClick={onUnapprove}>
                        Unapprove
                    </button>
                ) : !flagInfo ? (
                    <button
                        type="button"
                        className={`a-btn ghost flag-toggle ${showFlagForm ? 'on' : ''}`}
                        aria-pressed={showFlagForm}
                        onClick={handleMarkReviewClick}
                    >
                        {FlagIcon} Mark for review
                    </button>
                ) : null}
                <button
                    type="button"
                    className="a-btn primary"
                    onClick={onApprove}
                    disabled={!(target || '').trim()}
                >
                    {EIcn.check} Confirm &amp; next
                </button>
            </div>
        </div>
    );
};

// ============ INACTIVE SEGMENT ROW ============
const InactiveSegment = ({ index, source, target, status, tmDisplay, onActivate }) => (
    <button type="button" className="rs-ed-seg" onClick={() => onActivate(index)}>
        <div className="sn-col">
            <span className="sn-n">{index + 1}</span>
            <span className={`sn-dot ${status}`} />
        </div>
        <div className="src-pane">{source}</div>
        <div className={`tgt-pane ${status === 'untranslated' ? 'empty' : ''}`}>
            {status === 'untranslated' ? '—' : target}
        </div>
        <div className="meta-col">
            <span className={`tm-match ${tmCls(tmDisplay)}`}>{tmDisplay != null ? `${Math.round(tmDisplay)}%` : '—'}</span>
            <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
        </div>
    </button>
);

// ============ SEG LIST ============
const SegList = ({
    sections, translated, deriveStatus, confidence,
    active, setActive,
    sourceLang, targetLang, sourceDir, targetDir,
    glossTerms,
    aiSuggestion, aiSourceLabel, aiLoading,
    isApproved,
    onChangeTarget, onApprove, onUnapprove, onReject, onMarkReview,
    onAcceptAI, onDismissAI, onRegenerateAI,
    onBulkAI,
    pageSize, setPageSize,
    flaggedMap, onFlag, onResolveFlag,
}) => {
    const total = sections.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const activePage = Math.floor(active / pageSize);
    const [page, setPage] = React.useState(activePage);
    React.useEffect(() => { setPage(activePage); }, [activePage]);
    const safePage = Math.min(page, pageCount - 1);
    const visibleRange = useMemo(() => {
        const start = safePage * pageSize;
        const end = Math.min(total, start + pageSize);
        return { start, end };
    }, [safePage, pageSize, total]);

    return (
        <main className="rs-ed-main">
            <div className="rs-ed-main-h">
                <div className="h-info">
                    <b>Editing in-context</b>
                    <span>Showing {total === 0 ? 0 : visibleRange.start + 1}–{visibleRange.end} of {total} · click any to focus</span>
                </div>
                <span className="spacer" />
                <label className="btn-q" style={{ gap: 6, cursor: 'pointer' }}>
                    <span style={{ opacity: 0.7 }}>Per page</span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={{ appearance: 'none', border: 0, background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', paddingRight: 2 }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </label>
                <button
                    type="button"
                    className="btn-q"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={safePage === 0}
                    aria-label="Previous page"
                >‹</button>
                <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 60, textAlign: 'center' }}>
                    Page {safePage + 1} / {pageCount}
                </span>
                <button
                    type="button"
                    className="btn-q"
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={safePage >= pageCount - 1}
                    aria-label="Next page"
                >›</button>
                <button type="button" className="btn-q">{EIcn.filter}<span>Filter view</span></button>
                <button type="button" className="btn-q" onClick={onBulkAI}>{EIcn.ai}<span>Bulk AI draft</span></button>
            </div>
            <div className="rs-ed-segs">
                {Array.from({ length: visibleRange.end - visibleRange.start }, (_, k) => {
                    const i = visibleRange.start + k;
                    const status = deriveStatus(i);
                    if (i === active) {
                        return (
                            <ActiveSegment
                                key={i}
                                index={i}
                                total={total}
                                source={sections[i]}
                                target={translated[i] || ''}
                                isApproved={isApproved(i)}
                                confidence={confidence[i]}
                                sourceLang={sourceLang}
                                targetLang={targetLang}
                                sourceDir={sourceDir}
                                targetDir={targetDir}
                                glossTerms={glossTerms}
                                aiSuggestion={aiSuggestion}
                                aiSourceLabel={aiSourceLabel}
                                aiLoading={aiLoading}
                                onChange={(v) => onChangeTarget(i, v)}
                                onApprove={() => onApprove(i)}
                                onUnapprove={() => onUnapprove(i)}
                                onReject={() => onReject(i)}
                                onMarkReview={() => onMarkReview(i)}
                                onAcceptAI={(v) => onAcceptAI(i, v)}
                                onDismissAI={onDismissAI}
                                onRegenerateAI={onRegenerateAI}
                                flagInfo={flaggedMap[i] || null}
                                onFlag={onFlag}
                                onResolveFlag={onResolveFlag}
                            />
                        );
                    }
                    return (
                        <InactiveSegment
                            key={i}
                            index={i}
                            source={sections[i]}
                            target={translated[i] || ''}
                            status={status}
                            tmDisplay={confidence[i]}
                            onActivate={setActive}
                        />
                    );
                })}
            </div>
        </main>
    );
};

// ============ CONTEXT PANEL ============
const ContextPanel = ({
    activeTab, setActiveTab,
    activeIndex,
    tmMatches, historyMatches, tmLoading,
    glossTerms,
    comments,
    commentsState,
    openCommentCount,
    me,
    collaborators,
    onAddComment,
    onEditComment,
    onDeleteComment,
    onResolveComment,
    issues,
    onApplyTM, onApplyHistory,
    onOpenAnnotations,
}) => {
    const tabs = [
        ['matches', 'Matches', EIcn.memory, tmMatches.length + historyMatches.length],
        ['glossary', 'Glossary', EIcn.glossary, glossTerms.length],
        ['comments', 'Comments', EIcn.comment, openCommentCount || 0],
        ['issues', 'Issues', EIcn.alert, issues.filter((i) => i.kind === 'warn' || i.kind === 'err').length],
    ];

    return (
        <aside className="rs-ed-cx">
            <div className="rs-ed-cx-tabs">
                {tabs.map(([key, name, ic, ct]) => (
                    <button
                        key={key}
                        type="button"
                        className={`rs-ed-cx-tab ${activeTab === key ? 'on' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {ic}<span>{name}</span>
                        <span className="ct">{ct}</span>
                    </button>
                ))}
            </div>
            <div className="rs-ed-cx-body">
                {activeTab === 'matches' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            Translation memory · {tmLoading ? 'looking up…' : `${tmMatches.length} matches`}
                        </div>
                        {!tmLoading && tmMatches.length === 0 && historyMatches.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No matches yet for this segment.
                            </div>
                        )}
                        {tmMatches.map((m, i) => (
                            <div key={i} className="rs-ed-tm">
                                <div className="tm-head">
                                    <span className={`tm-m ${tmCls(m.confidence)}`}>{m.confidence != null ? `${Math.round(m.confidence)}%` : '—'}</span>
                                    <span className="tm-origin"><b>Memory</b></span>
                                    <button type="button" className="tm-apply" onClick={() => onApplyTM(m)}>Apply</button>
                                </div>
                                <div className="tm-src">{m.source}</div>
                                <div className="tm-tgt">{m.translatedText}</div>
                            </div>
                        ))}
                        {historyMatches.map((h, i) => (
                            <div key={`h${i}`} className="rs-ed-tm">
                                <div className="tm-head">
                                    <span className="tm-m">In-project</span>
                                    <span className="tm-origin"><b>This project</b></span>
                                    <button type="button" className="tm-apply" onClick={() => onApplyHistory(h)}>Apply</button>
                                </div>
                                <div className="tm-src">{h.source}</div>
                                <div className="tm-tgt">{h.target}</div>
                            </div>
                        ))}
                    </>
                )}

                {activeTab === 'glossary' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            Terms found in segment {activeIndex + 1}
                        </div>
                        {glossTerms.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No glossary terms match this segment.
                            </div>
                        )}
                        {glossTerms.map((g, i) => (
                            <div key={i} className="rs-ed-gl-item">
                                <div className="term-row">
                                    <span className="term-src">{g.source}</span>
                                    <span className="arr">→</span>
                                    <span className="term-tgt">{g.target || (g.ignore ? '— (ignored)' : '—')}</span>
                                    {g.ignore && <span className="term-lock">IGNORE</span>}
                                    {g.approved && <span className="term-lock">LOCKED</span>}
                                </div>
                                {g.context && <div className="term-def">{g.context}</div>}
                            </div>
                        ))}
                    </>
                )}

                {activeTab === 'comments' && (
                    <CommentsPanel
                        sectionIndex={activeIndex}
                        state={commentsState}
                        me={me}
                        collaborators={collaborators}
                        onAdd={onAddComment}
                        onEdit={onEditComment}
                        onDelete={onDeleteComment}
                        onResolve={onResolveComment}
                    />
                )}

                {activeTab === 'issues' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            QA checks on segment {activeIndex + 1}
                        </div>
                        {issues.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No issues found.
                            </div>
                        )}
                        {issues.map((iss, i) => (
                            <div key={i} className={`rs-ed-iss-item ${iss.kind}`}>
                                <span className="iss-ic">
                                    {iss.kind === 'ok' && EIcn.okCirc}
                                    {iss.kind === 'warn' && EIcn.warn}
                                    {iss.kind === 'err' && EIcn.alert}
                                </span>
                                <div className="iss-body">
                                    <div className="iss-t">{iss.title}</div>
                                    <div className="iss-d">{iss.desc}</div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </aside>
    );
};

// ============ FOOT BAR ============
const FootBar = ({ aiStatus }) => (
    <div className="rs-ed-foot">
        <span className="item"><span className="kbd">⌘</span><span className="kbd">⏎</span><span>Confirm &amp; next</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">↑</span><span className="kbd">↓</span><span>Move</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">/</span><span>AI suggest</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">G</span><span>Insert glossary</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">M</span><span>Apply top TM</span></span>
        <span className="spacer" />
        <span className="bdg-ai"><span className="dot" />Rush AI · {aiStatus}</span>
    </div>
);

// ============ MAIN ============
const RushEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);
    const user = useSelector((s) => s.auth.user) || {};

    let currentUserId;
    if (token) {
        try {
            currentUserId = jwtDecode(token).id;
        } catch (e) { /* ignore */ }
    }

    const queryParams = new URLSearchParams(location.search);
    const initialDocVersion = parseInt(queryParams.get('docVersion'), 10);
    const initialSegmentParam = parseInt(queryParams.get('segment'), 10);
    const initialSegmentIndex = Number.isNaN(initialSegmentParam) ? null : Math.max(0, initialSegmentParam - 1);

    // ---- Core state (mirrors TranslateProject) ----
    const [originalSections, setOriginalSections] = useState([]);
    const [translatedSections, setTranslatedSections] = useState([]);
    const [approvedSections, setApprovedSections] = useState([]);
    // Map<index, { reason, by, at }> for segments flagged for review
    const [flaggedSections, setFlaggedSections] = useState({});
    const [confidenceScores, setConfidenceScores] = useState({});
    const [translationSources, setTranslationSources] = useState([]);
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('');
    const [projectCreator, setProjectCreator] = useState('');
    const [projectName, setProjectName] = useState('');
    const [projectFile, setProjectFile] = useState({ type: 'file', name: '' });
    const [sectionLinks, setSectionLinks] = useState([]);
    const [tagMaps, setTagMaps] = useState([]);
    const [translationService, setTranslationService] = useState('');
    const [tone, setTone] = useState('default');
    const [docVersion, setDocVersion] = useState(isNaN(initialDocVersion) ? 0 : initialDocVersion);
    const [versionHistory, setVersionHistory] = useState([]);

    const [glossaries, setGlossaries] = useState([]);
    const [selectedGlossaryId, setSelectedGlossaryId] = useState(null);
    const [selectedGlossaryTerms, setSelectedGlossaryTerms] = useState([]);
    const [annotatedSections, setAnnotatedSections] = useState(new Set());
    const [selectedSectionAnnotations, setSelectedSectionAnnotations] = useState([]);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(initialSegmentIndex);
    const initialSegmentAppliedRef = useRef(false);
    const [isAnnotationPanelOpen, setIsAnnotationPanelOpen] = useState(false);
    const [grammarCheckResults, setGrammarCheckResults] = useState({});
    const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);

    // If we arrived with ?segment=N (e.g. from an inbox mention deep link),
    // clamp the requested segment once sections finish loading.
    useEffect(() => {
        if (initialSegmentIndex == null) return;
        if (initialSegmentAppliedRef.current) return;
        if (!originalSections || originalSections.length === 0) return;
        const clamped = Math.min(initialSegmentIndex, originalSections.length - 1);
        setSelectedSectionIndex(clamped);
        initialSegmentAppliedRef.current = true;
    }, [originalSections, initialSegmentIndex]);

    // --- Per-segment Comments thread (lifted state) ---
    // commentsBySeg: { [sectionIndex]: { loading, error, items } }
    const [commentsBySeg, setCommentsBySeg] = useState({});
    const [collaborators, setCollaborators] = useState([]);

    const [userPermissions, setUserPermissions] = useState({
        canUseMT: true, canEditTM: true, canEditGlossary: true, mtQuota: 1000000,
    });
    const [useTranslationMemory, setUseTranslationMemory] = useState(true);
    const [preferredAlgorithm, setPreferredAlgorithm] = useState('exact');

    const [suggestionData, setSuggestionData] = useState({ memory: [], glossary: [], ai: null, history: [] });
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const suggestionCache = useRef({});

    // Context Sync
    const [contextSyncMatches, setContextSyncMatches] = useState([]);
    const [showContextSyncAlert, setShowContextSyncAlert] = useState(false);
    const [showContextSyncModal, setShowContextSyncModal] = useState(false);
    const [contextSyncSourceIndex, setContextSyncSourceIndex] = useState(null);
    const [contextSyncNewTranslation, setContextSyncNewTranslation] = useState('');
    const contextSyncTimeoutRef = useRef(null);
    const segmentSaveTimeoutRef = useRef(null);

    // Project map overlay
    const [showMap, setShowMap] = useState(false);

    // Lightweight in-editor toast for write errors (and any future status pings).
    // Avoids pulling MUI Snackbar in for one message.
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const toastTimerRef = useRef(null);
    const notify = useCallback((message, severity = 'info') => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ open: true, message, severity });
        toastTimerRef.current = setTimeout(() => {
            setToast((t) => ({ ...t, open: false }));
        }, severity === 'error' ? 5000 : 3000);
    }, []);
    useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

    // UI
    const [active, setActive] = useState(0);
    const [pageSize, setPageSize] = useState(() => {
        const saved = parseInt(localStorage.getItem('rush-editor-page-size'), 10);
        return [10, 25, 50, 100].includes(saved) ? saved : 10;
    });
    useEffect(() => { localStorage.setItem('rush-editor-page-size', String(pageSize)); }, [pageSize]);
    const [navFilter, setNavFilter] = useState('All');
    const [cxTab, setCxTab] = useState('matches');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        document.body.classList.add('rs-editor-body');
        return () => document.body.classList.remove('rs-editor-body');
    }, []);

    useEffect(() => {
        if (userPermissions && !userPermissions.canEditTM) setUseTranslationMemory(false);
    }, [userPermissions]);

    // ---- Fetch user settings + permissions ----
    useEffect(() => {
        if (!token) return;
        axios.get('/api/user-settings', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setPreferredAlgorithm(res.data.preferredAlgorithm || 'exact'))
            .catch(() => { /* ignore */ });
        axios.get('/api/team/my-permissions', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => { if (res.data.permissions) setUserPermissions(res.data.permissions); })
            .catch(() => { /* ignore */ });
    }, [token]);

    // ---- Glossary selection (verbatim port from TranslateProject) ----
    const handleGlossarySelection = useCallback(async (glossaryId, persist = true) => {
        setSelectedGlossaryId(glossaryId);
        if (glossaryId) {
            try {
                const response = await axios.get(`/api/glossary/${glossaryId}/terms`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSelectedGlossaryTerms(response.data);
            } catch (err) {
                console.error('Failed to load glossary terms:', err);
            }
        } else {
            setSelectedGlossaryTerms([]);
        }
        if (persist) {
            try {
                await axios.put(`/api/projects/${id}`, { glossaryId: glossaryId || null },
                    { headers: { Authorization: `Bearer ${token}` } });
            } catch (err) {
                console.error('Error updating project glossary:', err);
            }
        }
    }, [id, token]);

    // ---- Fetch glossaries list ----
    useEffect(() => {
        if (!token) return;
        axios.get('/api/glossary', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setGlossaries(r.data || []))
            .catch(() => setGlossaries([]));
    }, [token]);

    // ---- Fetch annotations (set of annotated indices) ----
    useEffect(() => {
        if (!id || !token) return;
        axios.get(`/api/annotations/${id}/annotations`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                const annotatedIndices = new Set((res.data || []).map((a) => a.sectionIndex));
                setAnnotatedSections(annotatedIndices);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                }
            });
    }, [id, token, dispatch, navigate]);

    // ---- Fetch annotations for a specific section ----
    const fetchAnnotations = useCallback(async (sectionIndex) => {
        try {
            const response = await axios.get(`/api/annotations/${id}/annotations/${sectionIndex}`,
                { headers: { Authorization: `Bearer ${token}` } });
            setSelectedSectionAnnotations(response.data || []);
        } catch (err) {
            console.error('Error fetching annotations:', err);
            setSelectedSectionAnnotations([]);
        }
    }, [id, token]);

    const handleAnnotationOpen = (index) => {
        setSelectedSectionIndex(index);
        setIsAnnotationPanelOpen(true);
        fetchAnnotations(index);
    };
    const handleAnnotationClose = () => {
        setIsAnnotationPanelOpen(false);
        setSelectedSectionAnnotations([]);
    };
    const handleAnnotationAdded = (sectionIndex) => {
        setAnnotatedSections((prev) => new Set(prev).add(sectionIndex));
    };

    // ============ COMMENTS THREAD: handlers + socket wiring ============
    // Identity for the current user (used to label "my" comments).
    const me = useMemo(() => {
        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
        return { id: currentUserId, name };
    }, [user.firstName, user.lastName, user.email, currentUserId]);

    // Derive open-thread counts per segment (top-level, not deleted, not resolved).
    const openCountsBySeg = useMemo(() => {
        const out = {};
        Object.entries(commentsBySeg).forEach(([k, v]) => {
            const items = v?.items || [];
            out[k] = items.filter((c) => !c.parentId && !c.deleted && !c.resolved).length;
        });
        return out;
    }, [commentsBySeg]);

    // Mutator helper for replacing items for a section.
    const updateSegItems = useCallback((sectionIndex, mutator) => {
        setCommentsBySeg((prev) => {
            const cur = prev[sectionIndex] || { loading: false, error: null, items: [] };
            const nextItems = mutator(cur.items || []);
            return { ...prev, [sectionIndex]: { ...cur, items: nextItems } };
        });
    }, []);

    const fetchComments = useCallback(async (sectionIndex) => {
        if (!token || !id || sectionIndex == null) return;
        setCommentsBySeg((prev) => ({
            ...prev,
            [sectionIndex]: {
                ...(prev[sectionIndex] || { items: [] }),
                loading: true,
                error: null,
                items: prev[sectionIndex]?.items || [],
            },
        }));
        try {
            const res = await axios.get(
                `/api/projects/${id}/segments/${sectionIndex}/comments`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setCommentsBySeg((prev) => ({
                ...prev,
                [sectionIndex]: { loading: false, error: null, items: res.data || [] },
            }));
        } catch (err) {
            console.error('Error fetching comments:', err);
            setCommentsBySeg((prev) => ({
                ...prev,
                [sectionIndex]: {
                    loading: false,
                    error: err.response?.data?.message || 'Failed to load comments.',
                    items: prev[sectionIndex]?.items || [],
                },
            }));
        }
    }, [id, token]);

    const addComment = useCallback(async ({ sectionIndex, body, parentId }) => {
        if (!body || !body.trim()) return;
        const tmpId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const optimistic = {
            _id: tmpId,
            _optimistic: true,
            projectId: id,
            sectionIndex,
            author: {
                _id: me.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
            },
            parentId: parentId || null,
            body: body.trim(),
            mentions: [],
            resolved: false,
            deleted: false,
            createdAt: new Date().toISOString(),
            editedAt: null,
        };
        updateSegItems(sectionIndex, (items) => [...items, optimistic]);
        try {
            const res = await axios.post(
                `/api/projects/${id}/segments/${sectionIndex}/comments`,
                { body: body.trim(), parentId: parentId || null },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const saved = res.data;
            updateSegItems(sectionIndex, (items) => items.map((c) => (c._id === tmpId ? saved : c)));
        } catch (err) {
            console.error('Error posting comment:', err);
            updateSegItems(sectionIndex, (items) => items.filter((c) => c._id !== tmpId));
            notify(err.response?.data?.message || 'Could not post comment.', 'error');
        }
    }, [id, token, me.id, user.firstName, user.lastName, user.email, updateSegItems, notify]);

    const editComment = useCallback(async (commentId, body) => {
        if (!body || !body.trim()) return;
        // Locate the comment (any segment) to roll back if needed
        let prevBody = null;
        let segIdx = null;
        Object.entries(commentsBySeg).forEach(([k, v]) => {
            (v.items || []).forEach((c) => {
                if (String(c._id) === String(commentId)) {
                    prevBody = c.body;
                    segIdx = Number(k);
                }
            });
        });
        if (segIdx == null) return;
        updateSegItems(segIdx, (items) => items.map((c) => (
            String(c._id) === String(commentId)
                ? { ...c, body: body.trim(), editedAt: new Date().toISOString() }
                : c
        )));
        try {
            const res = await axios.patch(
                `/api/comments/${commentId}`,
                { body: body.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const updated = res.data;
            updateSegItems(segIdx, (items) => items.map((c) => (String(c._id) === String(commentId) ? updated : c)));
        } catch (err) {
            console.error('Error editing comment:', err);
            updateSegItems(segIdx, (items) => items.map((c) => (
                String(c._id) === String(commentId) ? { ...c, body: prevBody } : c
            )));
            notify(err.response?.data?.message || 'Could not save edit.', 'error');
        }
    }, [commentsBySeg, token, updateSegItems, notify]);

    const deleteComment = useCallback(async (comment) => {
        const commentId = comment._id;
        let segIdx = null;
        let snapshot = null;
        Object.entries(commentsBySeg).forEach(([k, v]) => {
            if ((v.items || []).some((c) => String(c._id) === String(commentId))) {
                segIdx = Number(k);
                snapshot = v.items.slice();
            }
        });
        if (segIdx == null) return;

        const hasChildren = (snapshot || []).some((c) => String(c.parentId) === String(commentId) && !c.deleted);
        if (hasChildren) {
            // Optimistic soft-delete
            updateSegItems(segIdx, (items) => items.map((c) => (
                String(c._id) === String(commentId) ? { ...c, body: null, deleted: true } : c
            )));
        } else {
            updateSegItems(segIdx, (items) => items.filter((c) => String(c._id) !== String(commentId)));
        }
        try {
            await axios.delete(`/api/comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {
            console.error('Error deleting comment:', err);
            if (snapshot) updateSegItems(segIdx, () => snapshot);
            notify(err.response?.data?.message || 'Could not delete comment.', 'error');
        }
    }, [commentsBySeg, token, updateSegItems, notify]);

    const setCommentResolved = useCallback(async (commentId, resolved) => {
        let segIdx = null;
        let prev = null;
        Object.entries(commentsBySeg).forEach(([k, v]) => {
            (v.items || []).forEach((c) => {
                if (String(c._id) === String(commentId)) {
                    segIdx = Number(k);
                    prev = c.resolved;
                }
            });
        });
        if (segIdx == null) return;
        updateSegItems(segIdx, (items) => items.map((c) => (
            String(c._id) === String(commentId) ? { ...c, resolved } : c
        )));
        try {
            const res = await axios.patch(
                `/api/comments/${commentId}`,
                { resolved },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const updated = res.data;
            updateSegItems(segIdx, (items) => items.map((c) => (String(c._id) === String(commentId) ? updated : c)));
        } catch (err) {
            console.error('Error toggling resolved:', err);
            updateSegItems(segIdx, (items) => items.map((c) => (
                String(c._id) === String(commentId) ? { ...c, resolved: prev } : c
            )));
        }
    }, [commentsBySeg, token, updateSegItems]);

    // Fetch collaborators on project load.
    useEffect(() => {
        if (!token || !id) return;
        axios.get(`/api/projects/${id}/collaborators`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setCollaborators(res.data || []))
            .catch(() => setCollaborators([]));
    }, [id, token]);

    // Socket: join the project room + listen for live comment events.
    // Reuses the global socket instance set by SignInCard/App (window.__SOCKET__).
    // TODO(comments): if a useSocket hook is introduced, swap this for it.
    useEffect(() => {
        if (!id) return undefined;
        const socket = typeof window !== 'undefined' ? window.__SOCKET__ : null;
        if (!socket) return undefined;
        socket.emit('joinProjectComments', id);

        const onCreated = (c) => {
            if (!c || String(c.projectId) !== String(id)) return;
            const seg = c.sectionIndex;
            setCommentsBySeg((prev) => {
                const cur = prev[seg] || { loading: false, error: null, items: [] };
                // Skip if already present (e.g. our own optimistic upgrade already happened)
                if ((cur.items || []).some((x) => String(x._id) === String(c._id))) return prev;
                return { ...prev, [seg]: { ...cur, items: [...(cur.items || []), c] } };
            });
        };
        const onUpdated = (c) => {
            if (!c || String(c.projectId) !== String(id)) return;
            const seg = c.sectionIndex;
            setCommentsBySeg((prev) => {
                const cur = prev[seg];
                if (!cur) return prev;
                return {
                    ...prev,
                    [seg]: {
                        ...cur,
                        items: (cur.items || []).map((x) => (String(x._id) === String(c._id) ? c : x)),
                    },
                };
            });
        };
        const onDeleted = (payload) => {
            if (!payload || String(payload.projectId) !== String(id)) return;
            const seg = payload.sectionIndex;
            setCommentsBySeg((prev) => {
                const cur = prev[seg];
                if (!cur) return prev;
                let nextItems;
                if (payload.soft && payload.comment) {
                    nextItems = (cur.items || []).map((x) => (String(x._id) === String(payload.id) ? payload.comment : x));
                } else {
                    nextItems = (cur.items || []).filter((x) => String(x._id) !== String(payload.id));
                }
                return { ...prev, [seg]: { ...cur, items: nextItems } };
            });
        };

        socket.on('comment:created', onCreated);
        socket.on('comment:updated', onUpdated);
        socket.on('comment:deleted', onDeleted);

        return () => {
            try { socket.emit('leaveProjectComments', id); } catch (e) { /* noop */ }
            socket.off('comment:created', onCreated);
            socket.off('comment:updated', onUpdated);
            socket.off('comment:deleted', onDeleted);
        };
    }, [id]);

    // ---- Fetch project (verbatim port) ----
    useEffect(() => {
        if (!token || !id) return;
        const fetchProject = async () => {
            try {
                const url = !isNaN(initialDocVersion)
                    ? `/api/projects/${id}?docVersion=${initialDocVersion}`
                    : `/api/projects/${id}`;
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                const { project } = response.data;
                setOriginalSections(project.sections || []);
                setTranslatedSections(project.translatedSections || []);
                setConfidenceScores(project.confidenceScores || {});
                setTranslationSources(project.translationSources || new Array((project.sections || []).length).fill(''));
                setSourceLanguage(project.sourceLanguage || '');
                setTargetLanguage(project.targetLanguage || '');
                setProjectCreator(project.createdBy);
                setApprovedSections(project.approvedSections || []);
                const flagMap = {};
                (project.flaggedSections || []).forEach((f) => {
                    if (f && typeof f.index === 'number') {
                        flagMap[f.index] = { reason: f.reason || null, by: f.by || null, at: f.at || null };
                    }
                });
                setFlaggedSections(flagMap);
                setSectionLinks(project.sectionLinks || []);
                setTagMaps(project.tagMaps || []);
                setTranslationService(project.translationService || 'deepl');
                setTone(project.tone || 'default');
                if (project.glossaryId) {
                    await handleGlossarySelection(project.glossaryId, false);
                }
                setProjectName(project.projectName || '');
                const path = project.fileUrl || project.templateFilePath || '';
                const filename = path.split('/').pop() || project.projectName;
                setProjectFile({ type: 'file', name: filename });

                const docVer = !isNaN(initialDocVersion) ? initialDocVersion : project.currentVersion || 0;
                setDocVersion(docVer);
                const history = (project.documentVersions || [])[docVer]?.translationVersions || [];
                setVersionHistory(history);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching project:', err);
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load project.');
                    setLoading(false);
                }
            }
        };
        fetchProject();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, token, initialDocVersion]);

    // ---- Derived ----
    const sections = originalSections;
    const total = sections.length;
    const sourceDir = isLanguageRTL(sourceLanguage) ? 'rtl' : 'ltr';
    const targetDir = isLanguageRTL(targetLanguage) ? 'rtl' : 'ltr';
    const srcLangDetails = getLanguageDetails(sourceLanguage);
    const tgtLangDetails = getLanguageDetails(targetLanguage);
    const srcLangCode = (srcLangDetails.code || sourceLanguage || '').toUpperCase();
    const tgtLangCode = (tgtLangDetails.code || targetLanguage || '').toUpperCase();

    // ---- Status derivation (mirrors TranslateProject deriveStatus) ----
    const deriveStatus = useCallback((globalIndex) => {
        // User-flagged for review takes precedence over every other status —
        // it's an active attention signal the user opted in to.
        if (flaggedSections[globalIndex]) return 'flagged';
        if (approvedSections.includes(globalIndex)) return 'approved';
        const t = (translatedSections[globalIndex] || '').trim();
        if (!t) return 'untranslated';
        if (annotatedSections.has(globalIndex)) return 'reviewed';
        return 'translated';
    }, [flaggedSections, approvedSections, translatedSections, annotatedSections]);

    const statusCounts = useMemo(() => {
        const counts = { All: sections.length, Untranslated: 0, Translated: 0, Reviewed: 0, Approved: 0, Flagged: 0 };
        sections.forEach((_, i) => {
            const s = deriveStatus(i);
            const key = s.charAt(0).toUpperCase() + s.slice(1);
            if (counts[key] != null) counts[key] += 1;
        });
        return counts;
    }, [sections, deriveStatus]);

    // ---- Active glossary matches (current segment) ----
    const activeGlossaryMatches = useMemo(() => {
        const src = (sections[active] || '').toLowerCase();
        if (!src) return [];
        return (selectedGlossaryTerms || []).filter((t) => src.includes((t.source || '').toLowerCase()));
    }, [sections, active, selectedGlossaryTerms]);

    // ---- Issues panel (mirrors TranslateProject activeIssues) ----
    const issues = useMemo(() => {
        const src = sections[active] || '';
        const tgt = translatedSections[active] || '';
        const out = [];
        const grammar = grammarCheckResults[active] || [];
        grammar.slice(0, 6).forEach((g) => {
            out.push({ kind: 'warn', title: g.shortMessage || g.rule?.description || 'Grammar issue', desc: g.message || '' });
        });
        if (src && tgt) {
            const ratio = tgt.length / Math.max(src.length, 1);
            if (ratio > 1.25) {
                out.push({ kind: 'warn', title: `Length over source by ${Math.round((ratio - 1) * 100)}%`, desc: 'Target translation is meaningfully longer than source — UI strings may overflow.' });
            } else if (ratio < 0.7) {
                out.push({ kind: 'warn', title: 'Target much shorter than source', desc: 'Target is materially shorter than source — verify nothing was dropped.' });
            } else {
                out.push({ kind: 'ok', title: 'Length within expected range', desc: 'Target length is close to source.' });
            }
        } else if (!tgt.trim()) {
            out.push({ kind: 'warn', title: 'Untranslated', desc: 'No target text yet — translate or accept an AI suggestion.' });
        }
        const srcTags = (src.match(/<\d+>|<\/\d+>/g) || []).length;
        const tgtTags = (tgt.match(/<\d+>|<\/\d+>/g) || []).length;
        if (srcTags || tgtTags) {
            if (srcTags === tgtTags) out.push({ kind: 'ok', title: 'Tags & placeholders preserved', desc: `${srcTags} tag${srcTags === 1 ? '' : 's'} carried over correctly to target.` });
            else out.push({ kind: 'warn', title: 'Tag count mismatch', desc: `Source has ${srcTags} tags, target has ${tgtTags}.` });
        }
        return out;
    }, [sections, translatedSections, grammarCheckResults, active]);

    // ---- Suggestions for segment (verbatim port from TranslateProject.fetchSuggestionsForSegment) ----
    const fetchSuggestionsForSegment = useCallback(async (index, revealAI = false) => {
        if (index === null || index === undefined) return;
        const originalText = originalSections[index];
        if (!originalText) return;
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        const cache = suggestionCache.current[index];

        const glossaryMatches = (selectedGlossaryTerms || []).filter((term) =>
            originalText.toLowerCase().includes((term.source || '').toLowerCase())
        );

        const historyMatches = [];
        const seenTargets = new Set();
        originalSections.forEach((text, i) => {
            if (i !== index && text === originalText && translatedSections[i] && !seenTargets.has(translatedSections[i])) {
                historyMatches.push({ source: text, target: translatedSections[i] });
                seenTargets.add(translatedSections[i]);
            }
        });

        const newSuggestions = {
            glossary: glossaryMatches,
            history: historyMatches.slice(0, 5),
            ai: cache?.ai || (historyMatches[0]?.target) || null,
            memory: cache?.memory || [],
        };
        setSuggestionData(newSuggestions);

        if (useTranslationMemory && (!cache || !cache.memory || cache.memory.length === 0)) {
            try {
                const memRes = await axios.post(
                    '/api/translation-memory/check',
                    { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                    { params: { algorithm: preferredAlgorithm }, headers: { Authorization: `Bearer ${token}` } },
                );
                if (memRes.data.translatedText) {
                    const memData = [{
                        translatedText: memRes.data.translatedText,
                        confidence: memRes.data.confidenceScore || 0,
                        source: originalText,
                    }];
                    setSuggestionData((prev) => {
                        const updated = { ...prev, memory: memData };
                        suggestionCache.current[index] = { ...suggestionCache.current[index], memory: memData };
                        return updated;
                    });
                }
            } catch (err) {
                console.error('Error fetching TM for suggestions:', err);
            }
        }

        if (revealAI && !cache?.ai) {
            if (!userPermissions.canUseMT) return;
            setIsGeneratingAI(true);
            try {
                const endpoint = translationService === 'openai' ? '/api/openai/translate' : '/api/translate';
                const aiRes = await axios.post(
                    endpoint,
                    { text: originalText, sourceLanguage: srcLang, targetLanguage, service: translationService || 'deepl', tone },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                if (aiRes.data.translatedText) {
                    const aiText = aiRes.data.translatedText;
                    setSuggestionData((prev) => {
                        const updated = { ...prev, ai: aiText };
                        suggestionCache.current[index] = { ...suggestionCache.current[index], ai: aiText };
                        return updated;
                    });
                }
            } catch (err) {
                console.error('Error fetching AI suggestion:', err);
            } finally {
                setIsGeneratingAI(false);
            }
        }
    }, [originalSections, translatedSections, selectedGlossaryTerms, sourceLanguage, targetLanguage,
        useTranslationMemory, currentUserId, preferredAlgorithm, token, translationService, tone,
        userPermissions.canUseMT]);

    // Trigger suggestion fetch when activating a segment.
    useEffect(() => {
        if (active != null && sections[active]) {
            fetchSuggestionsForSegment(active, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, sections.length]);

    // Lazy-load comments when switching to the comments tab OR moving segment.
    useEffect(() => {
        if (cxTab === 'comments' && active != null) {
            // Legacy annotations panel data — keep for backwards compat with AnnotationPanel.
            fetchAnnotations(active);
            // New per-segment Comments thread.
            fetchComments(active);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cxTab, active]);

    // ---- Local translation change w/ debounced server sync + context sync (verbatim port) ----
    const handleLocalTranslationChange = useCallback((globalIndex, newValue, confidence = null, source = null) => {
        setTranslatedSections((prev) => {
            const u = [...prev];
            u[globalIndex] = newValue;
            return u;
        });
        setConfidenceScores((prev) => ({ ...prev, [globalIndex]: confidence }));
        if (source) {
            setTranslationSources((prev) => {
                const u = [...prev];
                u[globalIndex] = source;
                return u;
            });
        }

        if (segmentSaveTimeoutRef.current) clearTimeout(segmentSaveTimeoutRef.current);
        segmentSaveTimeoutRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                await axios.put(`/api/projects/${id}/segment`, {
                    index: globalIndex,
                    translatedText: newValue,
                    confidence,
                    source,
                }, { headers: { Authorization: `Bearer ${token}` } });
            } catch (err) {
                console.error('Error saving segment change:', err);
            } finally {
                setIsSaving(false);
            }
        }, 800);

        if (contextSyncTimeoutRef.current) clearTimeout(contextSyncTimeoutRef.current);
        contextSyncTimeoutRef.current = setTimeout(() => {
            try {
                const matches = findSimilarSegments(
                    originalSections[globalIndex], originalSections, globalIndex,
                    translatedSections, newValue, 1.0,
                );
                if (matches.length > 0) {
                    setContextSyncMatches(matches);
                    setContextSyncSourceIndex(globalIndex);
                    setContextSyncNewTranslation(newValue);
                    setShowContextSyncAlert(true);
                } else {
                    setShowContextSyncAlert(false);
                }
            } catch (err) {
                // similarity failure is non-fatal
            }
        }, 1500);
    }, [id, token, originalSections, translatedSections]);

    // ---- Toggle approval (verbatim port; uses globalIndex directly) ----
    const toggleApproval = useCallback(async (globalIndex) => {
        const isApproved = approvedSections.includes(globalIndex);
        const updatedApproved = isApproved
            ? approvedSections.filter((i) => i !== globalIndex)
            : [...approvedSections, globalIndex];
        setApprovedSections(updatedApproved);
        try {
            await axios.put(`/api/projects/${id}/segment`, {
                index: globalIndex, approved: !isApproved,
            }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {
            console.error('Error saving toggle state:', err);
        }
    }, [approvedSections, id, token]);

    // ---- Single-segment translate (verbatim simplified port of handleTranslate) ----
    const handleTranslate = useCallback(async (globalIndex) => {
        const originalText = originalSections[globalIndex];
        if (!originalText) return;
        if (!userPermissions.canUseMT) return;
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;
        try {
            let translatedText = '';
            let confidence = 0;
            let source = '';
            if (useTranslationMemory) {
                try {
                    const mem = await axios.post(
                        '/api/translation-memory/check',
                        { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                        { params: { algorithm: preferredAlgorithm }, headers: { Authorization: `Bearer ${token}` } },
                    );
                    if (mem.data.translatedText) {
                        translatedText = mem.data.translatedText;
                        confidence = mem.data.confidenceScore || 0;
                        source = 'memory';
                    }
                } catch (e) { /* fall through */ }
            }
            if (!translatedText) {
                const endpoint = translationService === 'openai' ? '/api/openai/translate' : '/api/translate';
                const r = await axios.post(endpoint,
                    { text: originalText, sourceLanguage: srcLang, targetLanguage, service: translationService || 'deepl', tone },
                    { headers: { Authorization: `Bearer ${token}` } });
                translatedText = r.data.translatedText;
                confidence = 100;
                source = 'ai';
            }
            handleLocalTranslationChange(globalIndex, translatedText, confidence, source);
        } catch (err) {
            console.error('Error translating text:', err);
        }
    }, [originalSections, sourceLanguage, targetLanguage, useTranslationMemory, currentUserId,
        preferredAlgorithm, token, translationService, tone, handleLocalTranslationChange,
        userPermissions.canUseMT]);

    // Bulk AI draft for visible region around active segment
    const onBulkAI = useCallback(async () => {
        const start = Math.max(0, active - 4);
        const end = Math.min(total, active + 5);
        for (let i = start; i < end; i += 1) {
            if (!(translatedSections[i] || '').trim()) {
                // sequential to respect quota
                // eslint-disable-next-line no-await-in-loop
                await handleTranslate(i);
            }
        }
    }, [active, total, translatedSections, handleTranslate]);

    // Grammar check across project
    const handleGrammarCheck = useCallback(async () => {
        setIsCheckingGrammar(true);
        try {
            const locale = getLanguageToolLocale(sourceLanguage);
            const res = await axios.post('/api/grammar/check',
                { language: locale, sections: originalSections },
                { headers: { Authorization: `Bearer ${token}` } });
            const keyed = {};
            (res.data.results || []).forEach((r) => {
                const sorted = (r.matches || []).slice().sort((a, b) => a.offset - b.offset);
                keyed[r.index] = sorted;
            });
            setGrammarCheckResults(keyed);
        } catch (err) {
            console.error('Grammar check error:', err);
            setGrammarCheckResults({});
        } finally {
            setIsCheckingGrammar(false);
        }
    }, [sourceLanguage, originalSections, token]);

    // Save all + create version (verbatim port of handleSaveTranslation)
    const handleSaveTranslation = useCallback(async () => {
        setIsSaving(true);
        try {
            const totalSections = originalSections.length;
            const formattedTranslated = Array.from({ length: totalSections }, (_, i) => translatedSections[i] || '');
            const formattedApproved = approvedSections.filter((i) => i < totalSections);
            const formattedConfidence = Array.from({ length: totalSections }, (_, i) => confidenceScores[i] || 0);
            const isProjectOwner = (typeof projectCreator === 'object' ? projectCreator?._id : projectCreator?.toString()) === currentUserId?.toString();
            await axios.put(`/api/projects/${id}/translate`, {
                translatedSections: formattedTranslated,
                approvedSections: formattedApproved,
                confidenceScores: formattedConfidence,
                sections: isProjectOwner ? originalSections : undefined,
                createVersion: true,
            }, { headers: { Authorization: `Bearer ${token}` } });
            const { data } = await axios.get(`/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            const proj = data.project;
            const docVer = proj.currentVersion || 0;
            setDocVersion(docVer);
            setVersionHistory((proj.documentVersions || [])[docVer]?.translationVersions || []);
            clearTranslationDrafts();
        } catch (err) {
            console.error('Error saving translation:', err);
        } finally {
            setIsSaving(false);
        }
    }, [originalSections, translatedSections, approvedSections, confidenceScores,
        projectCreator, currentUserId, id, token]);

    // Approve all (verbatim port)
    const handleApproveAll = useCallback(async () => {
        try {
            const { data } = await axios.post(`/api/projects/${id}/approve-all`, {},
                { headers: { Authorization: `Bearer ${token}` } });
            const updatedApproved = data.approvedIndices || [];
            const merged = Array.from(new Set([...approvedSections, ...updatedApproved]));
            setApprovedSections(merged);
        } catch (err) {
            console.error('Error approving all segments:', err);
        }
    }, [id, token, approvedSections]);

    // Load specific version (verbatim port)
    const handleLoadVersion = useCallback(async (index) => {
        try {
            const params = [];
            if (!isNaN(docVersion)) params.push(`docVersion=${docVersion}`);
            if (index !== '' && index !== null && index !== undefined) params.push(`versionIndex=${index}`);
            const url = `/api/projects/${id}` + (params.length ? `?${params.join('&')}` : '');
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const { project } = response.data;
            setOriginalSections(project.sections || []);
            setTranslatedSections(project.translatedSections || []);
            setConfidenceScores(project.confidenceScores || {});
            setTranslationSources(project.translationSources || []);
            setApprovedSections(project.approvedSections || []);
            setSectionLinks(project.sectionLinks || []);
            setTagMaps(project.tagMaps || []);
            setProjectName(project.projectName || '');
        } catch (err) {
            console.error('Error loading version:', err);
        }
    }, [docVersion, id, token]);

    // Context sync replace handlers (verbatim port)
    const handleContextSyncReplaceSelected = useCallback(async (indices) => {
        const updatedTranslations = [...translatedSections];
        const updatePromises = indices.map(async (idx) => {
            updatedTranslations[idx] = contextSyncNewTranslation;
            return axios.put(`/api/projects/${id}/segment`, {
                index: idx,
                translatedText: contextSyncNewTranslation,
                confidence: 100,
                source: 'context-sync',
            }, { headers: { Authorization: `Bearer ${token}` } });
        });
        setTranslatedSections(updatedTranslations);
        try {
            await Promise.all(updatePromises);
        } catch (err) {
            console.error('Error in Context Sync bulk update:', err);
        }
        setShowContextSyncAlert(false);
    }, [translatedSections, contextSyncNewTranslation, id, token]);

    const handleContextSyncReplaceAll = useCallback(async () => {
        const segmentsToUpdate = contextSyncMatches.map((m) => m.index);
        await handleContextSyncReplaceSelected(segmentsToUpdate);
        setShowContextSyncAlert(false);
    }, [contextSyncMatches, handleContextSyncReplaceSelected]);

    // Map overlay segment click
    const handleMapSegmentClick = (index) => {
        setActive(index);
        setShowMap(false);
    };

    // Wire up RushEditor actions
    const onChangeTarget = (index, val) => handleLocalTranslationChange(index, val, null, null);

    const onApprove = (index) => {
        // Approve + advance to next according to navigator filter
        if (!approvedSections.includes(index)) toggleApproval(index);
        // advance to next visible nav index
        const navList = (navFilter === 'All')
            ? sections.map((_, i) => i)
            : sections.map((_, i) => i).filter((i) => STATUS_LABEL[deriveStatus(i)] === navFilter);
        const pos = navList.indexOf(index);
        const next = pos >= 0 ? navList[pos + 1] : null;
        if (next != null) setActive(next);
    };

    const onUnapprove = (index) => { if (approvedSections.includes(index)) toggleApproval(index); };

    const onReject = (index) => {
        handleLocalTranslationChange(index, '', null, null);
    };

    // Persist a flag/unflag for a segment, then advance on flag.
    const persistFlag = useCallback(async (index, flagged, reason = null) => {
        try {
            await axios.put(`/api/projects/${id}/segment`, {
                index, flagged, flagReason: reason,
            }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {
            console.error('Error saving flag state:', err);
        }
    }, [id, token]);

    const onFlag = useCallback((index, reason) => {
        const clean = (reason || '').trim();
        setFlaggedSections((prev) => ({
            ...prev,
            [index]: { reason: clean || null, by: null, at: new Date().toISOString() },
        }));
        persistFlag(index, true, clean || null);
        // Advance to next item in the current navigator filter (same logic as Confirm).
        const navList = (navFilter === 'All')
            ? sections.map((_, i) => i)
            : sections.map((_, i) => i).filter((i) => STATUS_LABEL[deriveStatus(i)] === navFilter);
        const pos = navList.indexOf(index);
        const next = pos >= 0 ? navList[pos + 1] : null;
        if (next != null) setActive(next);
    }, [navFilter, sections, deriveStatus, persistFlag]);

    const onResolveFlag = useCallback((index) => {
        setFlaggedSections((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
        persistFlag(index, false, null);
    }, [persistFlag]);

    // Mark-for-review now opens the inline form (state lifted to ActiveSegment).
    // Kept here as a no-op so the existing prop wiring stays intact.
    const onMarkReview = () => {};

    const onAcceptAI = (index, text) => {
        const conf = suggestionData.memory?.[0]?.confidence || 100;
        const src = suggestionData.ai ? 'ai' : 'memory';
        handleLocalTranslationChange(index, text, conf, src);
    };

    const onApplyTM = (m) => handleLocalTranslationChange(active, m.translatedText, m.confidence || 100, 'memory');
    const onApplyHistory = (h) => handleLocalTranslationChange(active, h.target, 100, 'history');

    // Keyboard shortcuts
    useEffect(() => {
        const onKey = (e) => {
            if (!sections.length) return;
            const meta = e.metaKey || e.ctrlKey;
            if (meta && e.key === '/') {
                e.preventDefault();
                fetchSuggestionsForSegment(active, true);
            } else if (meta && e.key === 'm') {
                e.preventDefault();
                const first = suggestionData.memory?.[0];
                if (first) onApplyTM(first);
            } else if (meta && e.key === '.') {
                e.preventDefault();
                setActive((a) => Math.min(sections.length - 1, a + 1));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections.length, active, suggestionData.memory]);

    // ---- AI suggestion derived ----
    const aiSuggestion = suggestionData.ai || suggestionData.memory?.[0]?.translatedText || '';
    const aiSourceLabel = suggestionData.ai ? 'Rush AI' : 'Translation memory';

    if (loading) return <div style={{ padding: 40 }} className="rs-app">Loading editor…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }} className="rs-app">{error}</div>;

    const fileExt = (projectFile?.name || '').includes('.')
        ? projectFile.name.split('.').pop().toLowerCase()
        : 'doc';

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)' }} className="rs-app">
                <TopBar
                    projectName={projectName}
                    fileExt={fileExt}
                    approvedCount={approvedSections.length}
                    total={total}
                    current={active}
                    sourceLang={srcLangCode || 'EN'}
                    targetLang={tgtLangCode || ''}
                    saving={isSaving}
                    onBack={() => navigate(`/dashboard/projects/${id}`)}
                />
                <div className="rs-ed-body">
                    <Navigator
                        sections={sections}
                        deriveStatus={deriveStatus}
                        active={active}
                        setActive={setActive}
                        filter={navFilter}
                        setFilter={setNavFilter}
                        statusCounts={statusCounts}
                    />
                    <SegList
                        sections={sections}
                        translated={translatedSections}
                        deriveStatus={deriveStatus}
                        confidence={confidenceScores}
                        active={active}
                        setActive={setActive}
                        sourceLang={srcLangCode || 'EN'}
                        targetLang={tgtLangCode || ''}
                        sourceDir={sourceDir}
                        targetDir={targetDir}
                        glossTerms={activeGlossaryMatches}
                        aiSuggestion={aiSuggestion}
                        aiSourceLabel={aiSourceLabel}
                        aiLoading={isGeneratingAI}
                        isApproved={(i) => approvedSections.includes(i)}
                        onChangeTarget={onChangeTarget}
                        onApprove={onApprove}
                        onUnapprove={onUnapprove}
                        onReject={onReject}
                        onMarkReview={onMarkReview}
                        onAcceptAI={onAcceptAI}
                        onDismissAI={() => setSuggestionData((p) => ({ ...p, ai: null }))}
                        onRegenerateAI={() => fetchSuggestionsForSegment(active, true)}
                        onBulkAI={onBulkAI}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        flaggedMap={flaggedSections}
                        onFlag={onFlag}
                        onResolveFlag={onResolveFlag}
                    />
                    <ContextPanel
                        activeTab={cxTab}
                        setActiveTab={setCxTab}
                        activeIndex={active}
                        tmMatches={suggestionData.memory || []}
                        historyMatches={suggestionData.history || []}
                        tmLoading={false}
                        glossTerms={activeGlossaryMatches}
                        comments={selectedSectionAnnotations}
                        commentsState={commentsBySeg[active] || { loading: false, error: null, items: [] }}
                        openCommentCount={openCountsBySeg[active] || 0}
                        me={me}
                        collaborators={collaborators}
                        onAddComment={addComment}
                        onEditComment={editComment}
                        onDeleteComment={deleteComment}
                        onResolveComment={setCommentResolved}
                        issues={issues}
                        onApplyTM={onApplyTM}
                        onApplyHistory={onApplyHistory}
                        onOpenAnnotations={() => handleAnnotationOpen(active)}
                    />
                </div>
                <FootBar aiStatus={isGeneratingAI ? 'thinking' : 'ready'} />
            </div>

            {/* Overlays mounted at root, like TranslateProject */}
            <AnnotationPanel
                projectId={id}
                sectionIndex={selectedSectionIndex}
                open={isAnnotationPanelOpen}
                onClose={handleAnnotationClose}
                onAnnotationAdded={handleAnnotationAdded}
            />
            <ContextSyncAlert
                isVisible={showContextSyncAlert}
                count={contextSyncMatches.length}
                onReplaceAll={handleContextSyncReplaceAll}
                onViewMatches={() => { setShowContextSyncModal(true); setShowContextSyncAlert(false); }}
                onIgnore={() => setShowContextSyncAlert(false)}
            />
            <ContextSyncModal
                open={showContextSyncModal}
                onClose={() => setShowContextSyncModal(false)}
                matches={contextSyncMatches}
                newTranslation={contextSyncNewTranslation}
                onReplaceSelected={handleContextSyncReplaceSelected}
            />
            <ProjectMapOverlay
                isOpen={showMap}
                onClose={() => setShowMap(false)}
                sections={originalSections}
                translatedSections={translatedSections}
                approvedSections={approvedSections}
                tagMaps={tagMaps}
                onSegmentClick={handleMapSegmentClick}
                currentSegmentIndex={active}
            />

            {/* Toast (in-editor lightweight snackbar) */}
            {toast.open && (
                <div
                    role={toast.severity === 'error' ? 'alert' : 'status'}
                    aria-live="polite"
                    style={{
                        position: 'fixed',
                        left: '50%',
                        bottom: 24,
                        transform: 'translateX(-50%)',
                        zIndex: 2100,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        minWidth: 280,
                        maxWidth: 'calc(100vw - 48px)',
                        background: 'var(--bg-card, #ffffff)',
                        border: '1px solid ' + (
                            toast.severity === 'error'
                                ? 'color-mix(in oklab, #c45252 40%, var(--line))'
                                : toast.severity === 'success'
                                    ? 'color-mix(in oklab, var(--green) 40%, var(--line))'
                                    : 'var(--line)'
                        ),
                        borderLeft: '3px solid ' + (
                            toast.severity === 'error' ? '#c45252'
                                : toast.severity === 'success' ? 'var(--green)'
                                    : 'var(--accent)'
                        ),
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(14,14,16,0.08), 0 2px 6px rgba(14,14,16,0.04)',
                        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                        color: 'var(--ink, #0e0e10)',
                        fontSize: 13,
                        fontWeight: 500,
                        animation: 'rsToastIn 160ms ease-out',
                    }}
                >
                    <span style={{ flexGrow: 1, minWidth: 0 }}>{toast.message}</span>
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => setToast((t) => ({ ...t, open: false }))}
                        style={{
                            appearance: 'none',
                            display: 'inline-grid',
                            placeItems: 'center',
                            width: 24,
                            height: 24,
                            background: 'transparent',
                            border: 0,
                            borderRadius: 5,
                            color: 'var(--ink-3, #6c6a68)',
                            cursor: 'pointer',
                            flexShrink: 0,
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                            <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                    </button>
                    <style>{`@keyframes rsToastIn { from { opacity: 0; transform: translateX(-50%) translateY(8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
                </div>
            )}

            {/* TODOs preserved for future wiring (not surfaced in design markup):
                - Glossary picker UI (handleGlossarySelection wired but no selector in design)
                - Version history selector (handleLoadVersion wired but no selector in design)
                - Full-project Save / Approve-all / Grammar-check trigger buttons
                  (handlers handleSaveTranslation, handleApproveAll, handleGrammarCheck are
                   wired and ready to attach when design exposes affordances).
                - Saving drafts to localStorage (omitted to keep state-source single).
            */}
        </>
    );
};

export default RushEditor;
